<template>
    <app-layout title="Customer Uploads">
        <page-header>
            <template #title>Uploads - {{ customer.name }}</template>

            <template #actions>
                <input
                    ref="fileInput"
                    type="file"
                    class="hidden"
                    multiple
                    @change="submitFiles"
                />
                <jet-button @click="$refs.fileInput.click()" :disabled="uploading">
                    Upload
                </jet-button>
                <jet-tertiary-button class="leading-none" :href="route('customers.index')">
                    Close
                </jet-tertiary-button>
            </template>
        </page-header>

        <customer-navigation :customer="customer" />

        <div class="px-8">
            <p v-if="uploadError" class="text-red-600 text-sm mb-4">{{ uploadError }}</p>
            <div class="rounded-t overflow-hidden border border-b-0">
                <table class="table w-full">
                    <thead>
                        <tr>
                            <th class="text-left">Name</th>
                            <th class="text-right w-0"></th>
                            <th class="text-right w-0"></th>
                            <th class="text-right w-0"></th>
                            <th class="text-right w-0"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="upload in uploads" :key="upload.id">
                            <td>{{ displayName(upload) }}</td>
                            <td class="text-right">
                                <a
                                    :href="fileUrl(upload)"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="inline-flex items-center justify-center px-4 py-2 bg-gray-light shadow-md rounded font-bold text-xs uppercase tracking-widest focus:outline-none focus:ring-green focus:ring focus:ring-opacity-50 transition hover:bg-gray mx-1"
                                >
                                    View
                                </a>
                            </td>
                            <td class="text-right">
                                <a
                                    :href="fileUrl(upload, true)"
                                    class="inline-flex items-center justify-center px-4 py-2 bg-gray-light shadow-md rounded font-bold text-xs uppercase tracking-widest focus:outline-none focus:ring-green focus:ring focus:ring-opacity-50 transition hover:bg-gray mx-1"
                                >
                                    Download
                                </a>
                            </td>
                            <td class="text-right">
                                <jet-button class="mx-1 whitespace-nowrap" @click="openRenameModal(upload)">
                                    Rename
                                </jet-button>
                            </td>
                            <td class="text-right">
                                <jet-danger-button class="mx-1" @click="confirmDelete(upload)">
                                    Delete
                                </jet-danger-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p v-if="uploads.length === 0" class="py-6 text-gray-600">
                No uploads yet. Use the Upload button to add menus, order guides, price lists, or other documents.
            </p>
        </div>

        <jet-dialog-modal :show="showRenameModal" @close="closeRenameModal" v-if="selectedUpload">
            <template #title>
                Rename upload
            </template>

            <template #content>
                <div class="mt-2">
                    <jet-label value="Name" />
                    <jet-input
                        ref="renameInput"
                        v-model="renameForm.name"
                        type="text"
                        class="mt-1 block w-full"
                        @keyup.enter="submitRename"
                    />
                    <jet-input-error :message="renameForm.errors.name" class="mt-1" />
                </div>
            </template>

            <template #footer>
                <jet-button @click="submitRename" :disabled="renameForm.processing">
                    Save
                </jet-button>
                <jet-tertiary-button @click="closeRenameModal">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>

        <jet-confirmation-modal :show="showDeleteModal" @close="closeDeleteModal" v-if="selectedUpload">
            <template #title>
                Delete upload
            </template>

            <template #content>
                <p>Are you sure you would like to delete <span class="font-bold">{{ selectedUpload ? displayName(selectedUpload) : '' }}</span>?</p>
            </template>

            <template #footer>
                <jet-danger-button @click="handleDelete" :disabled="deleteForm.processing">
                    Delete
                </jet-danger-button>
                <jet-tertiary-button class="ml-2" @click="closeDeleteModal">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import CustomerNavigation from '@/Shared/CustomerNavigation';
import JetButton from '@/Jetstream/Button';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';

export default {
    components: {
        AppLayout,
        PageHeader,
        CustomerNavigation,
        JetButton,
        JetTertiaryButton,
        JetDangerButton,
        JetDialogModal,
        JetConfirmationModal,
        JetInput,
        JetInputError,
        JetLabel,
    },

    props: ['customer', 'uploads'],

    data() {
        return {
            showRenameModal: false,
            showDeleteModal: false,
            selectedUpload: null,
            uploading: false,
            uploadError: null,
            renameForm: this.$inertia.form({
                name: '',
            }),
            deleteForm: this.$inertia.form(),
        };
    },

    methods: {
        displayName(upload) {
            return upload.name || upload.original_name || 'Untitled';
        },

        fileUrl(upload, download = false) {
            let url = route('customers.uploads.file', [this.customer.id, upload.id]);
            if (download) {
                url += '?download=1';
            }
            return url;
        },

        submitFiles(event) {
            const files = event.target.files;
            if (!files || !files.length) return;
            const formData = new FormData();
            Array.from(files).forEach(file => {
                formData.append('files[]', file);
            });
            this.uploadError = null;
            this.uploading = true;
            axios.post(route('customers.uploads.store', this.customer.id), formData)
                .then(() => {
                    this.$inertia.visit(route('customers.uploads.index', this.customer.id), { preserveScroll: true });
                })
                .catch((err) => {
                    if (err.response && err.response.data && err.response.data.errors) {
                        const errors = err.response.data.errors;
                        this.uploadError = errors.files ? errors.files[0] : err.response.data.message || 'Upload failed.';
                    } else {
                        this.uploadError = 'Upload failed. Please try again.';
                    }
                })
                .finally(() => {
                    this.uploading = false;
                    event.target.value = '';
                });
        },

        openRenameModal(upload) {
            this.selectedUpload = upload;
            this.renameForm.name = this.displayName(upload);
            this.renameForm.clearErrors();
            this.showRenameModal = true;
            this.$nextTick(() => {
                this.$refs.renameInput && this.$refs.renameInput.focus();
            });
        },

        closeRenameModal() {
            this.showRenameModal = false;
            this.selectedUpload = null;
            this.renameForm.reset();
        },

        submitRename() {
            if (!this.selectedUpload) return;
            this.renameForm.put(route('customers.uploads.update', [this.customer.id, this.selectedUpload.id]), {
                preserveScroll: true,
                onSuccess: () => {
                    this.closeRenameModal();
                },
            });
        },

        confirmDelete(upload) {
            this.selectedUpload = upload;
            this.showDeleteModal = true;
        },

        closeDeleteModal() {
            this.showDeleteModal = false;
            this.selectedUpload = null;
        },

        handleDelete() {
            if (!this.selectedUpload) return;
            this.deleteForm.delete(route('customers.uploads.destroy', [this.customer.id, this.selectedUpload.id]), {
                preserveScroll: true,
                onSuccess: () => {
                    this.closeDeleteModal();
                },
            });
        },
    },
};
</script>
